/**
 * ScheduleRegisterForcedPopup — app-level modal that prompts employees to register
 * their work schedule when the registration window is open and they haven't registered
 * for the target month yet.
 *
 * Mounted inside rootRoute (inside QueryClientProvider + LanguageProvider).
 * Renders nothing when: no session, outside window, already registered, or dismissed.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authClient } from '../lib/auth-client'
import { getScheduleStatus, registerSchedule } from '../lib/api-client'
import { Button } from '../components/ui/button'
import { useT } from '../i18n/language-provider'

function monthLabel(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

export function ScheduleRegisterForcedPopup() {
  const t = useT()
  const queryClient = useQueryClient()
  const { data: session, isPending } = authClient.useSession()
  const [dismissed, setDismissed] = useState(false)
  const [selected, setSelected] = useState<'8h30' | '9h'>('9h')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitDone, setSubmitDone] = useState(false)

  const enabled = !!session && !isPending

  const { data: status } = useQuery({
    queryKey: ['schedule-status'],
    queryFn: async () => {
      const res = await getScheduleStatus()
      if (!res.success) throw new Error(res.error.message)
      return res.data
    },
    enabled,
    // Refresh every 5 minutes — window may open/close
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: (schedule: string) => registerSchedule(schedule),
    onSuccess: (res) => {
      if (!res.success) {
        setSubmitError(res.error.message)
        return
      }
      setSubmitError(null)
      setSubmitDone(true)
      void queryClient.invalidateQueries({ queryKey: ['schedule-status'] })
      void queryClient.invalidateQueries({ queryKey: ['schedule-history'] })
    },
    onError: (err: Error) => setSubmitError(err.message),
  })

  // Don't render until we have all data
  if (!enabled || !status) return null

  // Popup conditions: window open + NOT yet registered for target month
  const shouldShow = status.canRegister && !status.registeredForTarget

  if (!shouldShow || dismissed || submitDone) return null

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    mutation.mutate(selected)
  }

  function handleLater() {
    setDismissed(true)
  }

  const targetLabel = monthLabel(status.target.year, status.target.month)

  return (
    /* Backdrop */
    <div id="schedule-register-popup" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Modal */}
      <div className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        {/* Header */}
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-fg">{t('schedule.popupTitle')}</h2>
          {status.isReopened && (
            <span className="ml-2 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              {t('schedule.reopened')}
            </span>
          )}
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-fg-soft">
            {t('schedule.popupDesc')} <strong>{targetLabel}</strong>.
          </p>

          <fieldset className="flex flex-col gap-3">
            {(['8h30', '9h'] as const).map((s) => (
              <label
                key={s}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors ${
                  selected === s
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-app text-fg-soft hover:bg-hover'
                }`}
              >
                <input
                  type="radio"
                  name="schedule"
                  value={s}
                  checked={selected === s}
                  onChange={() => setSelected(s)}
                  className="accent-primary"
                />
                <span className="font-medium">
                  {s === '8h30' ? t('schedule.schedule8h30') : t('schedule.schedule9h')}
                </span>
              </label>
            ))}
          </fieldset>

          <p className="text-xs text-faint">{t('schedule.popupNote')}</p>
          <p className="text-xs text-muted">
            {t('schedule.deadline')}: <strong>{status.deadline}</strong>
          </p>

          {submitError && <p className="text-sm text-red-600">{submitError}</p>}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              id="schedule-register-later-btn"
              type="button"
              onClick={handleLater}
              className="text-sm text-muted underline hover:text-fg-soft"
            >
              {t('schedule.later')}
            </button>
            <Button id="schedule-register-submit-btn" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t('common.saving') : t('schedule.registerBtn')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
